import { api } from 'api';
import { getNotifications, getPermission, getShopInfo, getShopPlanInfo } from 'api/service';
import { checkHasAnyOrders } from 'pages/analytics/api/analyticsOrders.api';
import { getShopInfoAdaptor } from 'adaptors/shop/shop-info';
import { planUsageAdaptor } from 'adaptors/plans/plan-usage.adaptor';
import { buildCampaignReportByStatus } from 'adaptors/dashboard/status-report.adaptor';
import { dashboardBootstrap } from 'rest/dist/gen/api/dashboard/dashboard';
import { perfMark } from 'utils/perfTrace';
import {
  FETCH_SHOPINFO,
  SET_ACCESSIBILITY_AND_CURRENT_PLAN,
  SET_PERMISSIONS_LOADING_FAILED,
  SET_CURRENT_PLAN,
  SET_HAS_ANY_ORDERS_EVER,
  SET_NOTIFICATIONS,
  UPDATE_CAN_ADD_ACTION,
  UPDATE_DASHBOARD_REPORT,
  UPDATE_DASHBOARD_REPORT_FAILED,
  UPDATE_DASHBOARD_REPORT_LOADING,
  UPDATE_SHOP_INFO,
} from '../constants';

// Replaces the 5 separate parallel requests App.js used to fire on first load
// (shops/info, shops/storage — both folded into getShopInfo already —
// pricing/planUsage, dashboard/campaignStatusDetail, analytics/orders/has-any)
// with ONE request to /api/dashboard/bootstrap. Those 5 (really 4 distinct
// round trips once shopInfo's own internal storage fetch is counted) competing
// for the browser's ~6-connections-per-origin budget (HTTP/1.1) during the
// exact window the dashboard's LCP is gated on was a measured contributor to
// slow first paint — see the [PageLoad][PERF] investigation in App.js.
// Dispatches the SAME action types the 4 individual actions below dispatch,
// reusing the SAME adaptors (getShopInfoAdaptor, planUsageAdaptor,
// buildCampaignReportByStatus) so every existing Redux consumer
// (state.shop.shopInfo, .accessibility, .currentPlan, .dashboardReport,
// .hasAnyOrdersEver) sees an identical shape — this only changes how many
// requests it takes to fill them in, not what ends up in the store.
export const getDashboardBootstrapAction = (dispatch) => async (host = '') => {
  perfMark('getDashboardBootstrapAction start');
  try {
    const res = await dashboardBootstrap();
    const { shopInfo, planUsage, shopStorage, dashboardReport, hasAnyOrders } = res?.data || {};

    dispatch({
      type: FETCH_SHOPINFO,
      // Under `data`, exactly like getShopInfo() in api/service.js: everything
      // reads shopInfo.data.* (money format, timezone, plan...). Spreading the
      // adapted fields at the top level left `data` undefined after every hard
      // refresh, so money values rendered blank until a focus refetch (the first
      // click) replaced it with the right shape.
      payload: { host, data: getShopInfoAdaptor(shopInfo, shopStorage || []) },
    });

    const permissions = planUsageAdaptor({ data: planUsage });
    dispatch({
      type: SET_ACCESSIBILITY_AND_CURRENT_PLAN,
      payload: {
        accessibility: {
          canAddCampaign: permissions?.data.canAddCampaign,
          trialEligible: permissions?.data.trialEligible,
          permissions: permissions?.data.permissions,
          countdownUsage: permissions?.data.countdownUsage,
        },
        currentPlan: permissions?.data.currentPlan,
        currentPlanFeatures: permissions?.data.currentPlanFeatures,
        currentPlanFeatureKeys: permissions?.data.currentPlanFeatureKeys,
        featureMinPlan: permissions?.data.featureMinPlan,
      },
    });

    const report = buildCampaignReportByStatus(
      { data: dashboardReport || [] },
      { data: planUsage }
    );
    if (report?.success) {
      dispatch({ type: UPDATE_DASHBOARD_REPORT, payload: report?.data });
    } else {
      dispatch({ type: UPDATE_DASHBOARD_REPORT_FAILED });
    }

    dispatch({ type: SET_HAS_ANY_ORDERS_EVER, payload: Boolean(hasAnyOrders) });
    perfMark('getDashboardBootstrapAction resolved');
  } catch (error) {
    // Mirrors each individual action's own failure branch, so a bootstrap
    // failure degrades the same way 4 separate failures would have.
    dispatch({ type: FETCH_SHOPINFO, payload: { host } });
    dispatch({ type: SET_PERMISSIONS_LOADING_FAILED });
    dispatch({ type: UPDATE_DASHBOARD_REPORT_FAILED });
    dispatch({ type: SET_HAS_ANY_ORDERS_EVER, payload: false });
    perfMark('getDashboardBootstrapAction failed', error);
  }
};

export const getShopInfoAction =
  (dispatch) =>
  async (host = '', isLoginPage = false, afterFetch) => {
    if (isLoginPage) {
      dispatch({
        type: FETCH_SHOPINFO,
        payload: {},
      });
      return;
    }
    // Shop info and permissions are independent reads (neither's response feeds
    // the other's request) — fire them together instead of chaining permissions
    // after shop info resolves. Both previously ran as a sequential waterfall,
    // which is what was gating App.js's `loadingShopInfo || loadingPermissions`
    // full-app spinner for the sum of both calls instead of the slower of the two.
    getShopInfo((success, data) => {
      if (success) {
        dispatch({
          type: FETCH_SHOPINFO,
          payload: { host, ...data },
        });
      } else {
        dispatch({ type: FETCH_SHOPINFO, payload: { host } });
      }
    });
    getPermissionAction(dispatch)(afterFetch);
    // NOTE: /api/common/getNotifications isn't implemented on the backend (returns 404),
    // and getShopInfoAction runs on every shop-info load — so this fired a useless 404
    // request constantly. Disabled until the notifications endpoint exists. The
    // getNotificationsAction below is still used by NotificationBanner's onDismiss.
    // getNotificationsAction(dispatch)();
  };
// All-time "has this shop ever had an order" check, into Redux instead of a
// page-local state — see App.js, which fires this (and getDasboardReport)
// in parallel with getShopInfoAction rather than letting the Dashboard route
// chunk mount before either request even starts (that serial chain — bundle
// → App Bridge handshake → shopInfo gate → lazy chunk download → THEN these
// two fetches — was the dominant contributor to the dashboard's LCP, per
// web-vitals attribution: ~2.5s of elementRenderDelay against ~110ms TTFB).
// `source` is diagnostic-only (labels which call site fired this in the
// [PageLoad][PERF] trail — App.js's early parallel prefetch vs Dashboard.js's
// own on-mount effect) — safe to omit, defaults to unlabelled.
export const checkHasAnyOrdersAction = (dispatch) => async (source = 'unspecified') => {
  perfMark(`checkHasAnyOrders start (${source})`);
  try {
    const res = await checkHasAnyOrders();
    dispatch({ type: SET_HAS_ANY_ORDERS_EVER, payload: Boolean(res?.hasAnyOrders) });
    perfMark(`checkHasAnyOrders resolved (${source})`);
  } catch {
    dispatch({ type: SET_HAS_ANY_ORDERS_EVER, payload: false });
    perfMark(`checkHasAnyOrders failed (${source})`);
  }
};
export const getNotificationsAction = (dispatch) => async (filter) => {
  getNotifications(filter, (success, data) => {
    if (success) {
      dispatch({
        type: SET_NOTIFICATIONS,
        payload: data.data,
      });
    }
  });
};
// `source` is diagnostic-only, same as checkHasAnyOrdersAction above. Note:
// this now actually returns the underlying fetch's promise (it previously
// didn't `await`/`return` the .then() chain, so callers awaiting it — none do
// today, but App.js's [PageLoad][PERF] marks need this — resolved instantly
// instead of when the request actually completed).
export const getDasboardReport = (dispatch) => async (disablePageLoading, source = 'unspecified') => {
  perfMark(`getDasboardReport start (${source})`);
  return api.getCampaignReportByStatus().then((res) => {
    if (res?.data?.success) {
      dispatch({
        type: UPDATE_DASHBOARD_REPORT,
        payload: res?.data?.data,
      });
    } else {
      dispatch({ type: UPDATE_DASHBOARD_REPORT_FAILED });
    }
    disablePageLoading && disablePageLoading();
    perfMark(`getDasboardReport resolved (${source})`);
  });
};
export const getCurrentPlanInfo = (dispatch) => async (afterFetch) => {
  getShopPlanInfo((success, data) => {
    if (success) {
      dispatch({
        type: SET_CURRENT_PLAN,
        payload: data?.data,
      });
      afterFetch && afterFetch();
    }
  });
};
export const getPermissionAction = (dispatch) => async (afterFetch) => {
  getPermission((success, data) => {
    if (success) {
      dispatch({
        type: SET_ACCESSIBILITY_AND_CURRENT_PLAN,
        payload: {
          accessibility: {
            canAddCampaign: data?.data.canAddCampaign,
            trialEligible: data?.data.trialEligible,
            permissions: data?.data.permissions,
            countdownUsage: data?.data.countdownUsage,
          },
          currentPlan: data?.data.currentPlan,

          currentPlanFeatures: data?.data.currentPlanFeatures,
          currentPlanFeatureKeys: data?.data.currentPlanFeatureKeys,
          featureMinPlan: data?.data.featureMinPlan,
        },
      });
    } else {
      dispatch({ type: SET_PERMISSIONS_LOADING_FAILED });
    }
    // Always run the continuation, even when the permission refresh fails. Callers pass
    // it to finalize their flow (reset a Save spinner, navigate, run afterSave/afterDelete).
    // Gating it on `success` left the Save button spinning forever whenever this secondary
    // request errored — the campaign had already saved, but the UI never recovered.
    afterFetch && afterFetch();
  });
};

export const updateShopInfo = (dispatch) => (data) => {
  dispatch({ type: UPDATE_SHOP_INFO, payload: { ...data } });
};
export const updateCanAddAction = (dispatch) => (data) => {
  dispatch({ type: UPDATE_CAN_ADD_ACTION, payload: data });
};
export const updateDashboardReportLoading = (dispatch) => (loading) => {
  dispatch({ type: UPDATE_DASHBOARD_REPORT_LOADING, payload: loading });
};
